import { useEffect, useMemo, useState } from 'react';
import { api, fetchAuditFor } from '../api';
import type { TaskComment } from '../domain/types';
import { useAppData } from '../AppData';
import { useAuth } from '../auth/AuthProvider';
import {
  BacklogTask,
  DIMENSION_KINDS,
  DIMENSION_LABELS,
  STATUS_LABELS,
  VISIBLE_STATUSES,
  TIPOS,
  URGENCIAS,
  DIFICULTADES,
  VALORACION_LABELS,
  TITULO_MAX,
} from '../domain/types';
import { fmtDate, toDateInput, nameByEmail, errMsg, taskHeading, taskSummary, diaLocal, fmtDia } from '../domain/util';
import AjusteInput, { formatAjusteSuggestion } from './AjusteInput';
import CopyButton from './CopyButton';

const EDITABLE_ADMIN = new Set([
  ...DIMENSION_KINDS,
  'tipo', 'urgencia', 'dificultad', 'horas_estimadas', 'horas_reales',
  'ajuste', 'status', 'titulo', 'description', 'enlace_documentos', 'assigned_to', 'start_date', 'deadline',
]);
// El asignado gestiona la ejecución del pedido, no su enunciado: el título y la
// descripción los define Coordinación. Debe coincidir con el array `ed` del
// branch no-admin de update_task (20260901120000): la UI espeja al RPC.
const EDITABLE_ASSIGNEE = new Set([
  'status', 'horas_estimadas', 'horas_reales', 'enlace_documentos', 'start_date',
]);

// Orden de campos: primero lo que edita un usuario regular, luego el resto.
const FIELD_ORDER = [
  'status', 'titulo', 'description', 'enlace_documentos', 'horas_estimadas', 'horas_reales', 'start_date',
  'deadline', 'assigned_to', 'urgencia', 'tipo', 'dificultad',
  'proyecto', 'direccion', 'espacio', 'cliente_interno', 'categoria', 'pieza', 'sede', 'ajuste',
];
/** Campos de texto largo que ocupan las dos columnas del grid / de la ficha. */
const FULL_WIDTH = new Set(['titulo', 'description', 'enlace_documentos']);
// Formatea un 'yyyy-mm-dd' (input date) a dd/mm/yyyy sin desfase de zona horaria.
const fmtYmd = (s: string) => (s ? s.split('-').reverse().join('/') : '—');

const FIELD_LABELS: Record<string, string> = {
  status: 'Estado', titulo: 'Título', description: 'Descripción', enlace_documentos: 'Enlace de documentos',
  horas_estimadas: 'Horas estimadas', horas_reales: 'Horas reales',
  start_date: 'Inicio', deadline: 'Entrega', assigned_to: 'Asignado a', urgencia: 'Urgencia', tipo: 'Tipo',
  dificultad: 'Dificultad', ajuste: 'Ajuste de', ...DIMENSION_LABELS,
};

export default function TaskModal({
  task,
  allTasks = [],
  onClose,
  onSaved,
}: {
  task: BacklogTask;
  allTasks?: BacklogTask[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { dimensions, users, reviewByTask } = useAppData();
  const { email, role } = useAuth();
  const admin = role === 'admin';
  const isAssignee = task.assigned_to.toLowerCase() === email;
  const isDone = task.status === 'done';

  // Revisión cargada ⇒ la tarea queda bloqueada: nadie (ni admin) cambia el estado.
  const review = reviewByTask[task.id];
  const reviewLocked = review?.status === 'reviewed';

  const baseEditable: Set<string> = admin
    ? EDITABLE_ADMIN
    : isAssignee && !isDone
    ? EDITABLE_ASSIGNEE
    : new Set();
  // Bloqueo de revisión: solo se congela el estado; el resto sigue editable.
  const editable: Set<string> = reviewLocked
    ? new Set([...baseEditable].filter((k) => k !== 'status'))
    : baseEditable;
  const canEdit = editable.size > 0;
  // Comentar es independiente de "done": admin o asignado siempre pueden comentar.
  const canComment = admin || isAssignee;

  const [form, setForm] = useState<Record<string, any>>({
    ...task,
    start_date: toDateInput(task.start_date),
    deadline: toDateInput(task.deadline),
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [history, setHistory] = useState<any[] | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [comments, setComments] = useState<TaskComment[] | null>(null);
  const [commentText, setCommentText] = useState('');
  const [posting, setPosting] = useState(false);

  // ¿Se está finalizando la tarea en este guardado?
  const finalizing = form.status === 'done' && task.status !== 'done';

  // Datos del encabezado. Se leen de `form` y no de `task` para que acompañen
  // lo que el admin está editando, igual que ya hacía el título.
  const heading = taskHeading(form.titulo, form.description, 140);
  const clasificacion = taskSummary({ categoria: form.categoria, pieza: form.pieza, sede: form.sede });
  // La chapa "Vencida" mira el estado en pantalla: al elegir "Finalizada"
  // desaparece antes de guardar, que es lo que espera quien la está marcando.
  // `form.deadline` ya es 'yyyy-mm-dd' (toDateInput), así que se compara contra
  // el día de hoy: el deadline vale hasta el final de su jornada.
  const overdue =
    !!form.deadline && diaLocal(new Date()) > form.deadline && !['done', 'cancelled'].includes(form.status);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { confirming ? setConfirming(false) : onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, confirming]);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const piezasForCat = useMemo(
    () => (dimensions.pieza || []).filter((p) => !form.categoria || p.categoria === form.categoria),
    [dimensions.pieza, form.categoria]
  );
  // La dirección queda atada al proyecto: si hay proyecto, se fuerza la del proyecto.
  const projDireccion = useMemo(
    () => new Map((dimensions.proyecto || []).map((p) => [p.name, p.direccion_asociada || ''])),
    [dimensions.proyecto]
  );
  useEffect(() => {
    if (form.proyecto) {
      const d = projDireccion.get(form.proyecto) || '';
      setForm((f) => (f.direccion === d ? f : { ...f, direccion: d }));
    }
  }, [form.proyecto, projDireccion]);

  const save = async () => {
    setErr('');
    setMsg('');
    // El título es obligatorio (check en backlog + guard en update_task). Sólo
    // un admin lo tiene en `editable`, así que este chequeo es su espejo en UI.
    if (editable.has('titulo') && !String(form.titulo || '').trim()) {
      return setErr('El título es obligatorio.');
    }
    // Al finalizar: horas reales y fecha de inicio obligatorias + confirmación.
    if (finalizing) {
      const missing: string[] = [];
      if (form.horas_reales === '' || form.horas_reales == null) missing.push('horas reales');
      if (!form.start_date) missing.push('fecha de inicio');
      if (missing.length) return setErr(`Para finalizar la tarea, cargá ${missing.join(' y ')}.`);
      setConfirming(true);
      return;
    }
    await doSave();
  };

  const doSave = async () => {
    setConfirming(false);
    setSaving(true);
    setErr('');
    try {
      const updates: Record<string, unknown> = {};
      editable.forEach((k) => {
        updates[k] = form[k] === '' ? '' : form[k];
      });
      const res = await api.updateTask({ id: task.id, updates });
      setMsg(res.changed ? `Guardado (${res.changed} cambios).` : 'Sin cambios.');
      onSaved();
    } catch (e) {
      setErr(errMsg(e));
    }
    setSaving(false);
  };

  const loadComments = async () => {
    try {
      setComments(await api.listComments(task.id));
    } catch (e) {
      setErr(errMsg(e));
    }
  };
  useEffect(() => {
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  const postComment = async () => {
    const body = commentText.trim();
    if (!body) return;
    setPosting(true);
    setErr('');
    try {
      await api.addComment({ taskId: task.id, body });
      setCommentText('');
      await loadComments();
    } catch (e) {
      setErr(errMsg(e));
    }
    setPosting(false);
  };

  const loadHistory = async () => {
    if (history) {
      setHistory(null);
      return;
    }
    try {
      setHistory(await fetchAuditFor(task.id));
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const editKeys = FIELD_ORDER.filter((k) => editable.has(k));
  // `titulo` y `description` salen de la ficha cuando son de solo lectura: el
  // título ya es el encabezado y la descripción tiene su propio bloque, que se
  // lee mejor que una celda de la grilla de dos columnas.
  const readKeys = FIELD_ORDER.filter((k) => !editable.has(k) && k !== 'titulo' && k !== 'description');
  const showBrief = !editable.has('description');

  // Control editable para un campo.
  const control = (k: string) => {
    switch (k) {
      case 'status':
        return (
          <select value={form.status} onChange={(e) => set('status', e.target.value)}>
            {(VISIBLE_STATUSES.includes(task.status) ? VISIBLE_STATUSES : [task.status, ...VISIBLE_STATUSES]).map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
            ))}
          </select>
        );
      case 'assigned_to':
        return (
          <select value={form.assigned_to} onChange={(e) => set('assigned_to', e.target.value)}>
            <option value="">—</option>
            {users.map((u) => <option key={u.email} value={u.email}>{nameByEmail(u.email, users)}</option>)}
          </select>
        );
      case 'titulo':
        return (
          <input
            type="text"
            value={form.titulo || ''}
            maxLength={TITULO_MAX}
            onChange={(e) => set('titulo', e.target.value)}
          />
        );
      case 'description':
        return <textarea rows={8} value={form.description} onChange={(e) => set('description', e.target.value)} />;
      case 'enlace_documentos':
        return (
          <input
            type="url"
            value={form.enlace_documentos || ''}
            placeholder="https://drive.google.com/…"
            onChange={(e) => set('enlace_documentos', e.target.value)}
          />
        );
      case 'horas_estimadas':
      case 'horas_reales':
        return <input type="number" min={0} step={0.5} value={form[k]} onChange={(e) => set(k, e.target.value)} />;
      case 'start_date':
      case 'deadline':
        return <input type="date" value={form[k]} onChange={(e) => set(k, e.target.value)} />;
      case 'urgencia':
        return <select value={form.urgencia || ''} onChange={(e) => set('urgencia', e.target.value)}><option value="">—</option>{URGENCIAS.map((x) => <option key={x} value={x}>{x}</option>)}</select>;
      case 'tipo':
        return <select value={form.tipo || ''} onChange={(e) => set('tipo', e.target.value)}><option value="">—</option>{TIPOS.map((x) => <option key={x} value={x}>{x}</option>)}</select>;
      case 'dificultad':
        return <select value={form.dificultad || ''} onChange={(e) => set('dificultad', e.target.value)}><option value="">—</option>{DIFICULTADES.map((x) => <option key={x} value={x}>{x}</option>)}</select>;
      case 'ajuste':
        return <AjusteInput value={form.ajuste || ''} onChange={(v) => set('ajuste', v)} tasks={allTasks} currentTaskId={task.id} users={users} />;
      case 'direccion':
        return (
          <select value={form.direccion || ''} disabled={!!form.proyecto} onChange={(e) => set('direccion', e.target.value)}>
            <option value="">—</option>
            {(dimensions.direccion || []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        );
      case 'pieza':
        return (
          <select value={form.pieza || ''} disabled={!form.categoria} onChange={(e) => set('pieza', e.target.value)}>
            <option value="">—</option>
            {piezasForCat.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        );
      default: // dimensiones simples: proyecto, espacio, cliente_interno, categoria, sede
        return (
          <select value={form[k] || ''} onChange={(e) => set(k, e.target.value)}>
            <option value="">—</option>
            {(dimensions[k] || []).map((d: any) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        );
    }
  };

  // Valor de solo lectura (ficha de datos).
  const displayNode = (k: string) => {
    const v = form[k];
    if (k === 'status') return <span className={`badge st-${v}`}>{STATUS_LABELS[v] || v}</span>;
    if (k === 'urgencia') return v ? <span className={`badge urg-${v}`}>{v}</span> : '—';
    if (k === 'assigned_to') return v ? nameByEmail(v, users) : '—';
    if (k === 'ajuste') {
      const ref = allTasks.find((t) => t.id === v && t.id !== task.id);
      return ref ? `${formatAjusteSuggestion(ref, users).title} (${v})` : v ? String(v) : '—';
    }
    if (k === 'enlace_documentos') {
      return v ? <a href={v} target="_blank" rel="noopener noreferrer">{v}</a> : '—';
    }
    if (k === 'start_date' || k === 'deadline') return v ? fmtDia(v) : '—';
    if (k === 'horas_estimadas' || k === 'horas_reales') return v === '' || v == null ? '—' : String(v);
    return v ? String(v) : '—';
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {/* El título es el encabezado: identifica la tarea de un vistazo y las
            chapas de abajo dan su estado sin tener que bajar a los campos.
            El h2 lleva SOLO el texto del título: los e2e lo resuelven por
            getByRole('heading', { name: titulo }). */}
        <header className="modal-head">
          <div className="modal-head-main">
            <div className="modal-eyebrow">
              <span>{canEdit ? 'Editar tarea' : 'Ver tarea'}</span>
              {clasificacion && <span>{clasificacion}</span>}
            </div>
            <h2 className="modal-title" title={heading}>{heading}</h2>
            <div className="modal-badges">
              <span className={`badge st-${form.status}`}>{STATUS_LABELS[form.status] || form.status}</span>
              {form.urgencia && <span className={`badge urg-${form.urgencia}`}>{form.urgencia}</span>}
              {overdue && <span className="badge urg-alta">Vencida</span>}
            </div>
          </div>
          <button className="modal-close" aria-label="Cerrar" onClick={onClose}>✕</button>
        </header>

        <dl className="modal-meta">
          <div><dt>Asignado a</dt><dd>{nameByEmail(form.assigned_to, users)}</dd></div>
          <div><dt>Entrega</dt><dd>{fmtYmd(form.deadline)}</dd></div>
          <div><dt>Proyecto</dt><dd>{form.proyecto || '—'}</dd></div>
        </dl>

        {task.status === 'rejected' && task.rejected_reason && (
          <div className="error" style={{ marginBottom: 12 }}>
            <strong>Motivo de rechazo:</strong> {task.rejected_reason}
          </div>
        )}

        {reviewLocked && (
          <div className="panel" style={{ marginBottom: 12, borderLeft: `4px solid ${review!.valoracion === 'aprobado' ? 'var(--ok, #16a34a)' : 'var(--danger, #dc2626)'}` }}>
            <strong>Revisión cargada:</strong>{' '}
            <span className={`badge val-${review!.valoracion}`}>{VALORACION_LABELS[review!.valoracion] || review!.valoracion}</span>
            {review!.descripcion && <div className="muted" style={{ marginTop: 6 }}>{review!.descripcion}</div>}
            <div className="hint" style={{ marginTop: 6 }}>La tarea está cerrada y no puede cambiar de estado.</div>
          </div>
        )}

        {/* El pedido primero, los campos de gestión después: quien abre la tarea
            lee qué hay que hacer y recién ahí toca estado y horas. */}
        {showBrief && (
          <section className="task-brief">
            <div className="task-brief-head">
              <span className="task-brief-label">Descripción</span>
              <CopyButton text={form.description} />
            </div>
            <p className="task-brief-body">{form.description || '—'}</p>
          </section>
        )}

        {editKeys.length > 0 && (
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            {editKeys.map((k) => (
              <div
                key={k}
                className={k === 'description' ? 'field-copy' : undefined}
                style={FULL_WIDTH.has(k) ? { gridColumn: '1 / -1' } : undefined}
              >
                <label>
                  {/* El espacio antes del asterisco importa: los e2e resuelven el
                      campo por normalize-space(label) === 'Título *'. */}
                  {k === 'titulo' ? <>{FIELD_LABELS[k]} <span className="req">*</span></> : FIELD_LABELS[k]}
                  {k === 'direccion' && form.proyecto && <span className="hint" style={{ display: 'inline', marginLeft: 4 }}>(del proyecto)</span>}
                </label>
                {/* Suelto entre label y control a propósito: ver CopyButton.tsx. */}
                {k === 'description' && <CopyButton text={form.description} />}
                {control(k)}
              </div>
            ))}
          </div>
        )}

        {readKeys.length > 0 && (
          <div style={{ marginTop: editKeys.length ? 16 : 0 }}>
            <div className="form-section" style={{ marginBottom: 8 }}>Datos de la tarea</div>
            <div className="ficha">
              {readKeys.map((k) => (
                <div key={k} className={FULL_WIDTH.has(k) ? 'full' : ''}>
                  <div className="k">{FIELD_LABELS[k]}</div>
                  <div className="v">{displayNode(k)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
          Creada por {nameByEmail(task.created_by, users)} · {fmtDate(task.created_at, true)}
          {task.updated_by && <> · Últ. cambio {nameByEmail(task.updated_by, users)} {fmtDate(task.updated_at, true)}</>}
          {task.ajustes_count > 0 && <> · Reaperturas: {task.ajustes_count}</>}
        </div>

        <div className="comments" style={{ marginTop: 16 }}>
          <div className="form-section" style={{ marginBottom: 8 }}>Comentarios</div>
          {comments === null ? (
            <p className="muted" style={{ margin: 0 }}>Cargando…</p>
          ) : comments.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>Sin comentarios.</p>
          ) : (
            comments.map((c) => (
              <div key={c.id} className="comment">
                <div className="comment-body">{c.body}</div>
                <span className="comment-meta">{nameByEmail(c.author_email, users)} · {fmtDate(c.created_at, true)}</span>
              </div>
            ))
          )}
          {canComment && (
            <div style={{ marginTop: 10 }}>
              <textarea
                rows={2}
                maxLength={4000}
                placeholder="Escribí un comentario…"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
              <div className="row" style={{ justifyContent: 'flex-end', marginTop: 6 }}>
                <button className="primary" disabled={posting || !commentText.trim()} onClick={postComment}>
                  {posting ? 'Enviando…' : 'Comentar'}
                </button>
              </div>
            </div>
          )}
        </div>

        {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
        {msg && <div className="ok" style={{ marginTop: 10 }}>{msg}</div>}

        <div className="row" style={{ justifyContent: 'space-between', marginTop: 14 }}>
          <button onClick={loadHistory}>{history ? 'Ocultar historial' : 'Ver historial'}</button>
          <div className="row">
            <button onClick={onClose}>Cerrar</button>
            {canEdit && <button className="primary" disabled={saving} onClick={save}>{saving ? 'Guardando…' : finalizing ? 'Finalizar tarea' : 'Guardar cambios'}</button>}
          </div>
        </div>

        {history && (
          <div style={{ marginTop: 14 }}>
            <h2>Historial</h2>
            {history.length === 0 && <p className="muted">Sin registros.</p>}
            {history.map((h, i) => (
              <div key={i} style={{ borderBottom: '1px solid var(--border)', padding: '6px 0', fontSize: 13 }}>
                <strong>{h.action}</strong>{h.field ? ` · ${h.field}` : ''}
                {h.old_value || h.new_value ? <> : {h.old_value || '∅'} → {h.new_value || '∅'}</> : null}
                <span className="muted"> · {nameByEmail(h.by, users)} · {fmtDate(h.timestamp, true)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirming && (
        <div className="modal-bg" style={{ zIndex: 60 }} onClick={() => setConfirming(false)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>Finalizar tarea</h2>
            <p className="muted" style={{ marginTop: 0 }}>
              Una vez finalizada, la tarea <strong>no se podrá editar</strong> (solo un administrador). Revisá los datos:
            </p>
            <div className="ficha" style={{ marginTop: 8 }}>
              <div className="full"><div className="k">Título</div><div className="v">{form.titulo || '—'}</div></div>
              <div className="full"><div className="k">Descripción</div><div className="v">{form.description || '—'}</div></div>
              <div><div className="k">Horas reales</div><div className="v">{form.horas_reales}</div></div>
              <div><div className="k">Fecha de inicio</div><div className="v">{fmtYmd(form.start_date)}</div></div>
              <div><div className="k">Entrega solicitada</div><div className="v">{fmtYmd(form.deadline)}</div></div>
              <div><div className="k">Estado</div><div className="v"><span className="badge st-done">Finalizada</span></div></div>
            </div>
            {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
            <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setConfirming(false)}>Cancelar</button>
              <button className="primary" disabled={saving} onClick={doSave}>{saving ? 'Finalizando…' : 'Confirmar y finalizar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
